import { useEffect, useState } from "react";
import DateInput from "@/components/DateInput";
import api, { fmtMoney, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PencilSimple, Check, X, Warning, FileXls } from "@phosphor-icons/react";
import { toast } from "sonner";
import NumberInput from "@/components/ui/NumberInput";

/**
 * Dialog reutilizable para ver detalle de un lote y editar contraparte/monto de sus items.
 *
 * Props:
 * - batch: {id, type, filename, count, created_at, ...}
 * - onClose: () => void
 * - onChange: () => void  (para refrescar la lista padre)
 */
export default function BatchDetailDialog({ batch, onClose, onChange }) {
  const [data, setData] = useState(null);
  const [contrapartes, setContrapartes] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [editRow, setEditRow] = useState(null);  // {id, contraparte_id, monto, caja_id, fecha}
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data: d } = await api.get(`/import-batches/${batch.id}/items`);
    setData(d);
    // Cargar contrapartes según tipo
    const isCompras = d.type === "compras";
    const [{ data: cps }, { data: cjs }] = await Promise.all([
      api.get(isCompras ? "/proveedores" : "/clientes"),
      api.get("/cajas"),
    ]);
    setContrapartes(cps);
    setCajas(cjs);
  };

  useEffect(() => {
    if (batch?.id) load();
    // eslint-disable-next-line
  }, [batch?.id]);

  const startEdit = (item) => {
    setEditRow({
      id: item.id,
      contraparte_id: item.contraparte_id,
      caja_id: item.caja_id || "",
      fecha: item.fecha,
      monto: item.monto ?? item.total ?? 0,
      concepto: item.concepto || "",
    });
  };

  const cancelEdit = () => setEditRow(null);

  const saveEdit = async () => {
    if (!editRow) return;
    setSaving(true);
    try {
      let endpoint;
      let payload;
      const t = data.type;
      if (t === "cobros") {
        endpoint = `/cobros-pagos/${editRow.id}`;
        payload = {
          contraparte_id: editRow.contraparte_id,
          caja_id: editRow.caja_id,
          fecha: editRow.fecha,
          monto: parseFloat(editRow.monto),
          concepto: editRow.concepto,
        };
      } else if (t === "ventas") {
        // Solo permitir reasignar cliente + fecha (reutilizar PUT /facturas/{id})
        const orig = data.items.find((i) => i.id === editRow.id);
        const nuevoCli = contrapartes.find((c) => c.id === editRow.contraparte_id);
        endpoint = `/facturas/${editRow.id}`;
        payload = {
          tipo: orig.tipo,
          numero: orig.numero,
          tipo_comprobante: orig.tipo_comprobante,
          fecha: editRow.fecha,
          contraparte_id: editRow.contraparte_id,
          contraparte_nombre: nuevoCli?.nombre || orig.contraparte_nombre,
          items: orig.items,
          iva: orig.iva,
          moneda: orig.moneda,
          cotizacion: orig.cotizacion,
          unidad_negocio: orig.unidad_negocio,
          observaciones: orig.observaciones,
        };
      } else if (t === "compras") {
        const orig = data.items.find((i) => i.id === editRow.id);
        const nuevoProv = contrapartes.find((c) => c.id === editRow.contraparte_id);
        endpoint = `/compras/${editRow.id}`;
        payload = {
          numero: orig.numero,
          tipo_comprobante: orig.tipo_comprobante,
          fecha: editRow.fecha,
          contraparte_id: editRow.contraparte_id,
          contraparte_nombre: nuevoProv?.nombre || orig.contraparte_nombre,
          items: orig.items,
          iva_pct: orig.iva_pct,
          percepciones: orig.percepciones,
          moneda: orig.moneda,
          cotizacion: orig.cotizacion,
          romaneo: orig.romaneo,
          observaciones: orig.observaciones,
        };
      } else {
        setSaving(false);
        return;
      }
      await api.put(endpoint, payload);
      toast.success("Ítem actualizado");
      setEditRow(null);
      await load();
      if (onChange) onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const renderRow = (item) => {
    const isEdit = editRow?.id === item.id;
    const t = data.type;
    const monto = item.monto ?? item.total ?? 0;
    const caja = item.caja_nombre || "-";

    if (isEdit) {
      return (
        <tr key={item.id} className="bg-[#F5F1E9]">
          <td className="px-3 py-2 font-mono text-[11px]">
            <DateInput value={editRow.fecha}
              onChange={(e) => setEditRow({ ...editRow, fecha: e.target.value })}
              className="rounded-sm h-7 text-[11px] font-mono"
            />
          </td>
          <td className="px-3 py-2 text-[11px] text-[#666]">
            {t === "ventas" ? `${item.tipo_comprobante} ${item.numero}` :
              t === "compras" ? `${item.tipo_comprobante || "-"} ${item.numero}` :
              (item.concepto || "-")}
          </td>
          <td className="px-3 py-2">
            <Select
              value={editRow.contraparte_id}
              onValueChange={(v) => setEditRow({ ...editRow, contraparte_id: v })}
            >
              <SelectTrigger className="rounded-sm h-7 text-[11px]"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-64">
                {contrapartes.map((c) => (
                  <SelectItem key={c.id} value={c.id} className="text-[12px]">{c.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </td>
          {t === "cobros" && (
            <td className="px-3 py-2">
              <Select
                value={editRow.caja_id}
                onValueChange={(v) => setEditRow({ ...editRow, caja_id: v })}
              >
                <SelectTrigger className="rounded-sm h-7 text-[11px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {cajas.map((c) => <SelectItem key={c.id} value={c.id} className="text-[12px]">{c.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </td>
          )}
          <td className="px-3 py-2 text-right">
            {t === "cobros" ? (
              <NumberInput step="0.01" value={editRow.monto}
                onChange={(e) => setEditRow({ ...editRow, monto: e.target.value })}
                className="rounded-sm h-7 text-[11px] font-mono text-right w-28 ml-auto"
              />
            ) : (
              <span className="font-mono">{fmtMoney(monto)}</span>
            )}
          </td>
          <td className="px-3 py-2 text-center whitespace-nowrap">
            <button
              onClick={saveEdit}
              disabled={saving}
              data-testid={`save-item-${item.id}`}
              className="inline-flex p-1 rounded-sm text-emerald-700 hover:bg-emerald-50"
            >
              <Check size={14} weight="bold" />
            </button>
            <button
              onClick={cancelEdit}
              className="inline-flex p-1 rounded-sm text-neutral-600 hover:bg-neutral-100 ml-1"
            >
              <X size={14} weight="bold" />
            </button>
          </td>
        </tr>
      );
    }

    return (
      <tr key={item.id} data-testid={`bitem-${item.id}`} className="group hover:bg-neutral-50">
        <td className="px-3 py-2 font-mono text-[11px]">{fmtDate(item.fecha)}</td>
        <td className="px-3 py-2 text-[11px] text-[#666]">
          {t === "ventas" && (
            <>
              <span className="text-[10px] text-[#999]">{item.tipo_comprobante}</span>{" "}
              <span className="font-mono">{item.numero}</span>
              {item.unidad_negocio && (
                <span className="ml-2 text-[10px] font-mono bg-[#F5F1E9] text-[#6B1F24] px-1 py-0.5 rounded-sm">
                  {item.unidad_negocio}
                </span>
              )}
            </>
          )}
          {t === "compras" && (
            <>
              <span className="text-[10px] text-[#999]">{item.tipo_comprobante || "-"}</span>{" "}
              <span className="font-mono">{item.numero}</span>
            </>
          )}
          {t === "cobros" && (item.concepto || "-")}
        </td>
        <td className="px-3 py-2 truncate max-w-[220px]" title={item.contraparte_nombre}>{item.contraparte_nombre}</td>
        {t === "cobros" && (
          <td className="px-3 py-2 text-[11px] text-[#666]">{caja}</td>
        )}
        <td className="px-3 py-2 text-right font-mono font-semibold">
          {fmtMoney(monto, item.moneda || "ARS")}
        </td>
        <td className="px-3 py-2 text-center">
          <button
            data-testid={`edit-item-${item.id}`}
            onClick={() => startEdit(item)}
            className="p-1 hover:bg-neutral-200 rounded-sm text-[#666] hover:text-[#6B1F24] opacity-60 group-hover:opacity-100"
          >
            <PencilSimple size={13} />
          </button>
        </td>
      </tr>
    );
  };

  const isCompras = data?.type === "compras";
  const cpLabel = isCompras ? "Proveedor" : "Cliente";

  return (
    <Dialog open={!!batch} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <FileXls size={18} weight="bold" className="text-[#6B1F24]" />
            Detalle del lote: {batch?.filename}
          </DialogTitle>
          <DialogDescription>
            {data && (
              <span className="text-[12px] font-mono">
                Cargado: {new Date(batch.created_at).toLocaleString("es-AR")} ·{" "}
                {data.items.length} ítem(s) ·{" "}
                {batch.count && `${batch.count} creados`}
                {data.no_encontradas?.length > 0 && (
                  <span className="text-red-700 ml-2">
                    · {data.no_encontradas.length} no encontrados
                  </span>
                )}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {!data ? (
          <div className="p-8 text-center text-[#999] text-sm">Cargando…</div>
        ) : (
          <div className="space-y-4">
            {data.no_encontradas?.length > 0 && (
              <div className="border border-red-200 bg-red-50 rounded-sm p-3">
                <div className="text-[11px] uppercase font-mono text-red-700 tracking-widest mb-2 flex items-center gap-2">
                  <Warning size={14} weight="fill" />
                  No subidos al sistema ({data.no_encontradas.length})
                </div>
                <div className="max-h-32 overflow-y-auto text-[11px] font-mono text-red-800 space-y-0.5">
                  {data.no_encontradas.map((c, i) => <div key={i}>· {c}</div>)}
                </div>
                <div className="text-[11px] text-red-700 mt-2">
                  Estas referencias no matcheron con ninguna factura registrada. Cargá primero
                  la venta correspondiente y luego re-importá el archivo (o cargá el cobro
                  manualmente desde la cuenta corriente).
                </div>
              </div>
            )}

            <div className="border border-[#E5E5E5] rounded-sm overflow-hidden">
              <div className="px-4 py-2 bg-neutral-50 border-b flex items-center justify-between">
                <span className="text-[11px] uppercase font-mono text-[#666] tracking-widest">
                  Subidos al sistema ({data.items.length})
                </span>
                <span className="text-[11px] text-[#999] font-mono">
                  Click en el lápiz para editar
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead className="text-[11px] uppercase font-mono text-[#666] bg-neutral-50 border-b">
                    <tr>
                      <th className="px-3 py-2 text-left w-24">Fecha</th>
                      <th className="px-3 py-2 text-left">Referencia</th>
                      <th className="px-3 py-2 text-left w-56">{cpLabel}</th>
                      {data.type === "cobros" && <th className="px-3 py-2 text-left w-40">Caja</th>}
                      <th className="px-3 py-2 text-right w-28">Monto</th>
                      <th className="px-3 py-2 text-center w-16">Editar</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F0F0F0]">
                    {data.items.map(renderRow)}
                    {data.items.length === 0 && (
                      <tr><td colSpan={6} className="text-center py-6 text-[#999]">Sin ítems.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            onClick={onClose}
            variant="outline"
            className="rounded-sm"
          >
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
